import { css, } from "@emotion/react";
import compact from "lodash/compact";
import isFunction from "lodash/isFunction";
import isNumber from "lodash/isNumber";
import isObject from "lodash/isObject";
import merge from "lodash/merge";
import { P, match } from "ts-pattern";
import { shorthandCssProperties, } from "../types/css";
import { breakpoints, containerBreakpoints, containerBreakpointsMapping } from "../../theme";
// Helper function to handle the logic of checking the type of value and returning the appropriate value
const resolveValue = (value, theme) => typeof value === "number" ? theme.spacing(value) : value;
const isKeyframes = (value) => {
    return value.anim !== undefined;
};
const isArrayCssInterpolation = (value) => {
    return Array.isArray(value);
};
export const resolveCssObject = (theme, styles) => {
    /**
     * NOTICE: This function does not follow immutability practices.
     * This is done to avoid creating a new object or spreading the accumulated styles on every iteration.
     * An object of this potential size and repetitive spreading in an reduction has high potential for performance issues.
     * Spreading on every iteration would result in O(n^2). Whereas this approach remains linear (O(n) or O(nx)).
     * This is a trade-off between immutability and performance and should NOT become standard practice.
     */
    const accumulatedStyles = {};
    for (const property of Object.keys(styles)) {
        const rawValue = styles[property];
        const value = isFunction(rawValue) ? rawValue(theme) : rawValue;
        if (isArrayCssInterpolation(value)) {
            //LIMITATION: Ideally this additional check should not be necessary but the original emotion types could not be satisfied. Currently, styles that are of type ArrayCssInterpolation are not resolved.
            accumulatedStyles[property] = undefined;
        }
        else if (
        // If the value is an object, it is either a nested style object or a function. If it is a function, it is resolved to a CSS value. If it is a nested style object, it is recursively resolved.
        isObject(value)) {
            accumulatedStyles[property] = isKeyframes(value)
                ? value
                : resolveCssObject(theme, value);
        }
        else if (
        // Check if the property is a shorthand property and if the value is a number. These can be simply resolved to a CSS value if both conditions are met.
        (isNumber(value) || typeof value === "string") &&
            shorthandCssProperties.includes(property)) {
            const borderProperties = [
                "border",
                "borderTop",
                "borderRight",
                "borderBottom",
                "borderLeft",
                "outline",
            ];
            const paddingProperties = ["paddingX", "paddingY"];
            const marginProperties = ["marginX", "marginY"];
            match(property)
                .with(P.union(...borderProperties), (borderProperty) => {
                accumulatedStyles[borderProperty] =
                    typeof value === "number" ? `${value}px solid` : value;
            })
                .with(P.union(...paddingProperties), (paddingProperty) => {
                const resolvedPaddingValue = resolveValue(value, theme);
                accumulatedStyles[`${paddingProperty === "paddingX" ? "paddingLeft" : "paddingTop"}`] = resolvedPaddingValue;
                accumulatedStyles[`${paddingProperty === "paddingX" ? "paddingRight" : "paddingBottom"}`] = resolvedPaddingValue;
            })
                .with(P.union(...marginProperties), (marginProperty) => {
                const resolvedMarginValue = resolveValue(value, theme);
                accumulatedStyles[`${marginProperty === "marginX" ? "marginLeft" : "marginTop"}`] = resolvedMarginValue;
                accumulatedStyles[`${marginProperty === "marginX" ? "marginRight" : "marginBottom"}`] = resolvedMarginValue;
            })
                .otherwise(() => {
                accumulatedStyles[property] = resolveValue(value, theme);
            });
        }
        else {
            accumulatedStyles[property] = value;
        }
    }
    return accumulatedStyles;
};
const resolveBreakpoints = (theme, responsiveStyles) => responsiveStyles
    ? compact([
        ...breakpoints.map((breakpoint) => {
            const responsiveStyle = responsiveStyles[breakpoint];
            return (responsiveStyle && {
                [`@media (min-width: ${theme.breakpoints[breakpoint]}px)`]: resolveCssObject(theme, responsiveStyle),
            });
        }),
        ...containerBreakpoints.map((breakpoint) => {
            const responsiveStyle = responsiveStyles[breakpoint];
            return (responsiveStyle && {
                [`@container (min-width: ${theme.breakpoints[containerBreakpointsMapping[breakpoint]]}px)`]: resolveCssObject(theme, responsiveStyle),
            });
        }),
    ])
    : [];
const resolveContainerBreakpoints = (theme, responsiveStyles) => responsiveStyles
    ? compact(breakpoints.map((breakpoint) => {
        const responsiveStyle = responsiveStyles[breakpoint];
        return (responsiveStyle && {
            [`@container (min-width: ${theme.breakpoints[breakpoint]}px)`]: resolveCssObject(theme, responsiveStyle),
        });
    }))
    : [];
const resolveSxProp = (sx, theme) => {
    if (!sx || !theme) {
        return [];
    }
    //Casted to ensure better intellisense as the type is inferred as CssObject
    const { breakpoints, containerBreakpoints, ...resolvedSx } = sx;
    const typedResolvedSx = resolvedSx;
    //Shorthand resolution is first to give breakpoint styles higher specificity
    return [
        resolveCssObject(theme, typedResolvedSx),
        ...resolveBreakpoints(theme, breakpoints),
        ...resolveContainerBreakpoints(theme, containerBreakpoints),
    ];
};
export const resolveCss = ({ theme, sx, }) => css(resolveSxProp(sx, theme));
export const mergeSx = (baseSx, overridingSx) => merge({}, baseSx, overridingSx);
//# sourceMappingURL=sx.js.map